/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */
import React, { useMemo } from 'react';

import { jsx } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { token } from '@atlaskit/tokens';

import { isJiraProduct } from '../../../../common/utils/is-jira-product';
/* eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports */
import { SearchResult } from '../search-result';
/* eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports */
import { SearchResultsListDefaultRenderer } from '../search-results-list-default-renderer';
/* eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports */
import type { SearchResultsListRendererProps } from '../types';

import { SearchResultsLiteIssueTable } from './search-results-lite-issue-table';
import { SearchResultsLiteProjectTable } from './search-results-lite-project-table';

const styles = cssMap({
	liteResultsWrapper: {
		display: 'flex',
		flexDirection: 'column',
		// Total height = viewport minus top nav (56px) and fully-expanded filter bar (134px)
		// (need to set an explicit height here so that table containers respect max height styles)
		// @ts-expect-error - calc() not in cssMap's type union
		height: `calc(100vh - 190px)`,
		gap: token('space.300'),
		width: '100%',
	},
	invisibleBottomPadding: {
		flexShrink: 0,
		display: 'block',
		height: '1px',
		width: '1px',
	},
});

const JIRA_ISSUE_TYPENAME = 'SearchResultJiraIssue';
const JIRA_PROJECT_TYPENAME = 'SearchResultJiraProject';
const MAX_ROWS = 50;

export const SearchResultsListLiteRenderer = ({
	containsSearchResults,
	selectedProduct,
	edgesToDisplay,
	thirdPartyConfigs,
	availableProducts,
	originalQuery,
	logError,
	is3pGroupingEnabled,
	smartAnswersPortalRef,
	mainContentWidth,
	cloudId,
	appMode,
	searchQuery,
	primaryProduct,
	primarySubProduct,
	searchSessionId,
	linkedPRs,
	pastMeetingOccurrences,
	teamNames,
	issueStatuses,
	wasContentClearedBySpaceChange,
	clearFilters,
	clearSpaceFilter,
	fetchNextPage,
	cursor,
	isLoadingNext,
	getSelectedFilters,
}: SearchResultsListRendererProps): React.JSX.Element => {
	const jiraIssues = useMemo(
		() => edgesToDisplay.filter((edge) => edge?.node?.__typename === JIRA_ISSUE_TYPENAME),
		[edgesToDisplay],
	);
	const jiraIssuesPruned = jiraIssues.slice(0, MAX_ROWS);

	const jiraProjectsOrSpaces = useMemo(
		() => edgesToDisplay.filter((edge) => edge?.node?.__typename === JIRA_PROJECT_TYPENAME),
		[edgesToDisplay],
	);
	const jiraProjectsOrSpacesPruned = jiraProjectsOrSpaces.slice(0, MAX_ROWS);

	// Edges that are neither Jira issues nor Jira projects/spaces (e.g. boards,
	// filters, dashboards, plans). The lite tables only render issues and
	// projects, so any other Jira nouns must fall back to the default renderer
	// underneath the tables. Order is preserved relative to the original
	// `edgesToDisplay` because the filter is a single pass over that array.
	const otherJiraEdges = useMemo(
		() =>
			edgesToDisplay.filter(
				(edge) =>
					edge?.node?.__typename !== JIRA_ISSUE_TYPENAME &&
					edge?.node?.__typename !== JIRA_PROJECT_TYPENAME,
			),
		[edgesToDisplay],
	);

	const hasJiraIssues = !!jiraIssues.length;
	const hasJiraProjectsOrSpaces = !!jiraProjectsOrSpaces.length;
	const hasOtherJiraEdges = !!otherJiraEdges.length;

	// Determine if we should render the new "lite" search results experience based on the following:
	//   1. the experiment is enabled (already verified by the parent component /search-results/search-results.tsx)
	// 	 2. a Jira product should be selected in the app filter
	//   3. there are results to show
	//   4. the results contain either Jira issues, projects, or spaces
	//
	// If a Jira product is selected and the response contains ONLY non-issue/non-project
	// nouns (e.g. only boards), we fall back to the default renderer for the whole page
	// so the user still sees their results instead of an empty lite shell.
	const shouldRenderLiteList = useMemo(() => {
		const jiraProductIsSelected = selectedProduct && isJiraProduct(selectedProduct, true);
		return (
			jiraProductIsSelected && containsSearchResults && (hasJiraIssues || hasJiraProjectsOrSpaces)
		);
	}, [containsSearchResults, selectedProduct, hasJiraIssues, hasJiraProjectsOrSpaces]);

	const list = shouldRenderLiteList ? (
		<Box xcss={styles.liteResultsWrapper}>
			{hasJiraIssues && (
				<SearchResultsLiteIssueTable
					shownCount={jiraIssuesPruned.length}
					totalCount={jiraIssues.length}
					isOnlyTableShown={hasJiraIssues && !hasJiraProjectsOrSpaces}
				>
					{jiraIssuesPruned.map(
						(edge, position) =>
							edge?.node && (
								<SearchResult
									key={edge.node.id}
									node={edge.node}
									position={position}
									cloudId={cloudId}
									appMode={appMode}
									searchQuery={searchQuery}
									thirdPartyConfigs={thirdPartyConfigs}
									selectedProduct={selectedProduct}
									primaryProduct={primaryProduct}
									primarySubProduct={primarySubProduct}
									searchSessionId={searchSessionId}
									originalQuery={originalQuery}
									availableProducts={availableProducts}
									logError={logError}
									compassTeamName={teamNames[edge.node.id]}
									jiraIssueStatus={issueStatuses[edge.node.id]}
									jiraLinkedPRs={linkedPRs[edge.node.id]}
									pastMeetingOccurrences={pastMeetingOccurrences?.[edge.node.id]}
									is3pGroupingEnabled={is3pGroupingEnabled}
									smartAnswersPortalRef={smartAnswersPortalRef}
									mainContentWidth={mainContentWidth}
									// Single source of truth for lite-mode rendering: only set
									// inside this lite-list code path (a Jira product is selected
									// and there are results), so the lite table row cannot leak
									// onto the "All" tab via the default list renderer.
									isLiteMode
								/>
							),
					)}
				</SearchResultsLiteIssueTable>
			)}

			{hasJiraProjectsOrSpaces && (
				<SearchResultsLiteProjectTable
					shownCount={jiraProjectsOrSpacesPruned.length}
					totalCount={jiraProjectsOrSpaces.length}
					isOnlyTableShown={hasJiraProjectsOrSpaces && !hasJiraIssues}
				>
					{jiraProjectsOrSpacesPruned.map(
						(edge, position) =>
							edge?.node && (
								<SearchResult
									key={edge.node.id}
									node={edge.node}
									position={position}
									cloudId={cloudId}
									appMode={appMode}
									searchQuery={searchQuery}
									thirdPartyConfigs={thirdPartyConfigs}
									selectedProduct={selectedProduct}
									primaryProduct={primaryProduct}
									primarySubProduct={primarySubProduct}
									searchSessionId={searchSessionId}
									originalQuery={originalQuery}
									availableProducts={availableProducts}
									logError={logError}
									compassTeamName={teamNames[edge.node.id]}
									jiraIssueStatus={issueStatuses[edge.node.id]}
									jiraLinkedPRs={linkedPRs[edge.node.id]}
									pastMeetingOccurrences={pastMeetingOccurrences?.[edge.node.id]}
									is3pGroupingEnabled={is3pGroupingEnabled}
									smartAnswersPortalRef={smartAnswersPortalRef}
									mainContentWidth={mainContentWidth}
									// See note above on the issues table for why this is the
									// single source of truth for lite-mode rendering.
									isLiteMode
								/>
							),
					)}
				</SearchResultsLiteProjectTable>
			)}

			{/*
				Render any non-issue / non-project Jira nouns (boards, filters,
				dashboards, plans) underneath the lite tables using the default
				list renderer. The order of these edges is preserved from the
				original response.
			*/}
			{hasOtherJiraEdges && (
				<SearchResultsListDefaultRenderer
					containsSearchResults={containsSearchResults}
					edgesToDisplay={otherJiraEdges}
					cloudId={cloudId}
					appMode={appMode}
					searchQuery={searchQuery}
					thirdPartyConfigs={thirdPartyConfigs}
					selectedProduct={selectedProduct}
					primaryProduct={primaryProduct}
					primarySubProduct={primarySubProduct}
					searchSessionId={searchSessionId}
					originalQuery={originalQuery}
					availableProducts={availableProducts}
					logError={logError}
					teamNames={teamNames}
					issueStatuses={issueStatuses}
					linkedPRs={linkedPRs}
					is3pGroupingEnabled={is3pGroupingEnabled}
					smartAnswersPortalRef={smartAnswersPortalRef}
					mainContentWidth={mainContentWidth}
					wasContentClearedBySpaceChange={wasContentClearedBySpaceChange}
					clearSpaceFilter={clearSpaceFilter}
					clearFilters={clearFilters}
					getSelectedFilters={getSelectedFilters}
					cursor={cursor}
					isLoadingNext={isLoadingNext}
					fetchNextPage={fetchNextPage}
				/>
			)}
			{/* 
				Due to margin collapsing and the way overflows are handled in the HTML markup we add an invisible element here 
				for padding at the bottom of the page. This element is purely for visual purposes and is hidden from a11y technologies.
			*/}
			<div css={styles.invisibleBottomPadding} aria-hidden="true"></div>
		</Box>
	) : (
		<SearchResultsListDefaultRenderer
			containsSearchResults={containsSearchResults}
			edgesToDisplay={edgesToDisplay}
			cloudId={cloudId}
			appMode={appMode}
			searchQuery={searchQuery}
			thirdPartyConfigs={thirdPartyConfigs}
			selectedProduct={selectedProduct}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			searchSessionId={searchSessionId}
			originalQuery={originalQuery}
			availableProducts={availableProducts}
			logError={logError}
			teamNames={teamNames}
			issueStatuses={issueStatuses}
			linkedPRs={linkedPRs}
			pastMeetingOccurrences={pastMeetingOccurrences}
			is3pGroupingEnabled={is3pGroupingEnabled}
			smartAnswersPortalRef={smartAnswersPortalRef}
			mainContentWidth={mainContentWidth}
			wasContentClearedBySpaceChange={wasContentClearedBySpaceChange}
			clearSpaceFilter={clearSpaceFilter}
			clearFilters={clearFilters}
			getSelectedFilters={getSelectedFilters}
			cursor={cursor}
			isLoadingNext={isLoadingNext}
			fetchNextPage={fetchNextPage}
		/>
	);

	if (fg('rovo_search_results_granular_ufo_segments')) {
		return <UFOSegment name="search-page-results-list-lite">{list}</UFOSegment>;
	}

	return list;
};
